import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { Loader2, AlertCircle, ShieldCheck, Eye, EyeOff } from "lucide-react";
import AuthLayout from "../components/auth/AuthLayout";
import PasswordStrength from "../components/auth/PasswordStrength";
import api, { formatApiError } from "../lib/api";

export default function AcceptInvite() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  const [form, setForm] = useState({ first_name: "", last_name: "", password: "", confirm: "" });
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!token) { setErr("Missing token"); return; }
    api.get(`/api/firm/invitation-info?token=${token}`)
      .then(({ data }) => setInfo(data))
      .catch((e) => setErr(formatApiError(e)));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirm) { setErr("Passwords don't match"); return; }
    setErr(""); setLoading(true);
    try {
      await api.post("/api/firm/team/accept-invitation", {
        token, first_name: form.first_name, last_name: form.last_name, password: form.password,
      });
      setSuccess(true);
      setTimeout(() => nav("/login"), 2000);
    } catch (e) { setErr(formatApiError(e)); }
    finally { setLoading(false); }
  };

  if (success) {
    return (
      <AuthLayout title="Welcome to Compliche" subtitle={`You've joined ${info?.firm_name}. Redirecting to sign in…`}>
        <div className="text-center py-4"><ShieldCheck size={40} className="text-emerald2 mx-auto mb-2" /><Link to="/login" className="btn btn-primary btn-block mt-2">Sign in now</Link></div>
      </AuthLayout>
    );
  }

  if (!info && !err) {
    return <AuthLayout title="Loading invitation…"><div className="flex justify-center py-6"><Loader2 size={28} className="animate-spin text-indigo2" /></div></AuthLayout>;
  }

  if (err && !info) {
    return <AuthLayout title="Invalid invitation"><p className="t-body text-slate2 mb-5">{err}</p><Link to="/login" className="btn btn-secondary btn-block">Back to sign in</Link></AuthLayout>;
  }

  return (
    <AuthLayout title={`Join ${info.firm_name}`} subtitle={`${info.invited_by_name} invited you as ${info.role.replace("_", " ")}.`} testid="accept-invite-page">
      {err && <div className="mb-3 p-3 rounded-md text-sm" style={{ background: "rgba(239,68,68,0.06)", color: "#991B1B", border: "1px solid #FECACA" }}><AlertCircle size={14} className="inline mr-1" />{err}</div>}
      <form onSubmit={submit} className="space-y-3">
        <div><label className="label2">Email</label><input className="input2" value={info.email} disabled /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="label2">First name<span className="req">*</span></label><input className="input2" required value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} /></div>
          <div><label className="label2">Last name<span className="req">*</span></label><input className="input2" required value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} /></div>
        </div>
        <div>
          <label className="label2">Choose a password<span className="req">*</span></label>
          <div className="relative">
            <input type={showPw ? "text" : "password"} required className="input2 pr-12" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
            <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate2">{showPw ? <EyeOff size={16} /> : <Eye size={16} />}</button>
          </div>
          <PasswordStrength password={form.password} />
        </div>
        <div><label className="label2">Confirm password<span className="req">*</span></label><input type={showPw ? "text" : "password"} required className="input2" value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} /></div>
        <button type="submit" className="btn btn-primary btn-block" disabled={loading}>{loading ? <><Loader2 size={14} className="animate-spin" />Creating account…</> : "Accept & create account"}</button>
      </form>
    </AuthLayout>
  );
}
